'use es6';

import { Component } from 'react';
import { flatten, findWhere, throttle } from 'underscore';
import { getWidgetElement } from 'InpageEditorUI/lib/inpage/utils';
import WidgetWrapperContainer from 'InpageEditorUI/js/containers/WidgetWrapperContainer';
import FlexColumnDragAffordance from 'InpageEditorUI/components/FlexColumnDragAffordance';
import FlexColumnEmptyDropPlaceHolder from 'InpageEditorUI/components/FlexColumnEmptyDropPlaceHolder';
import BeforeBeginPortal from 'ContentEditorUI/components/portals/BeforeBeginPortal';
import AfterBeginPortal from 'ContentEditorUI/components/portals/AfterBeginPortal';
import AfterEndPortal from 'ContentEditorUI/components/portals/AfterEndPortal';
import { LameNullErrorHandlerInIframe } from 'ContentEditorUI/components/lameErrorHandling';
import { InpageModes } from 'ContentEditorUI/lib/app/constants';
import { View } from 'foundations-components/components/View';
import { Z_INDEX_MAX_LAYERS } from 'InpageEditorUI/lib/layoutSection/constants';
import styled from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FlexColumnControlsOverlay = styled.div.withConfig({
  displayName: "FlexColumn__FlexColumnControlsOverlay"
})(({
  $width,
  $height,
  $top
}) => ({
  position: 'absolute',
  zIndex: Z_INDEX_MAX_LAYERS.LEVEL_2,
  inlineSize: $width,
  blockSize: $height,
  insetBlockStart: $top
}));

/**
 * @param {Object} props.container (required)
 * @param {Element} props.containerElement (optional)
 * @param {Function} props.updateFlexColumnContents (required)
 * @param {Function} props.updateCurrentDropTarget (required)
 * @param {number} props.currentTargetDropIndex (optional)
 * @param {Function} props.startDraggingFromFlexColumn (required)
 * @param {Function} props.endDraggingFromFlexColumn (required)
 * @param {boolean} props.isDragging (optional)
 * @param {number} props.widgetBeingDraggedIndex (optional)
 * @param {string} props.widgetBeingDraggedParentContainer (optional)
 * @param {Array} props.inpageModulesInsideContainers (required)
 * @param {Array} props.gates (required)
 * @param {Object} props.bus (required)
 * @param {string} props.selectedModuleName (optional)
 * @param {string} props.highlightedWidgetId (optional)
 * @param {boolean} props.isAnythingSelected (required)
 * @param {string} props.scrollToModuleId (optional)
 * @param {boolean} props.sidebarIsExpanded (optional)
 * @param {string} props.currentInpageMode (required)
 * @param {string} props.renderLanguage (required)
 * @param {boolean} props.hasUndoRedoAccess (required)
 * @param {Function} props.tellParentToHideModuleMenu (required)
 * @param {Function} props.shouldShowFakeWidgetEditors (required)
 * @param {Function} props.updateNodePositions (required)
 * @param {Function} props.layoutSectionHoverPositionsClearedAndFrozen (required)
 * @param {Function} props.layoutSectionHoverPositionsThawed (required)
 * @param {Array} props.scopes (required)
 * @param {number} props.userId (required)
 */
export default class FlexColumn extends Component {
  constructor() {
    super();
    this.overlays = {};
    this.repositionOverlays = throttle(this.repositionOverlays.bind(this), 100);
  }
  componentDidMount() {
    window.addEventListener('resize', this.repositionOverlays);
  }
  componentDidUpdate(prevProps) {
    // We need to reposition the flex columns any time the inline drop zones
    // appear or hide because it affects the offsetTop position of the widgetElement and overlays.
    // The drop zones showing or hiding is delayed by a setTimeout to account for a different
    // Chrome bug, we need to defer the repositioning of the DOM here to wait the dropZones to
    // have been removed from the DOM to get an accurate offsetTop measurement.
    if (prevProps.isDragging !== this.props.isDragging) {
      setTimeout(() => this.repositionOverlays(), 0);
    }
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.repositionOverlays);
  }
  repositionOverlays() {
    Object.keys(this.overlays).forEach(key => {
      const {
        overlayElement,
        thisWidgetElement,
        nextWidgetElement,
        previousWidgetElement
      } = this.overlays[key];
      const {
        top,
        width,
        height
      } = this.calculateOverlayControlSizesTakingIntoAccountMargin(thisWidgetElement, nextWidgetElement, previousWidgetElement);
      overlayElement.style.top = `${top}px`;
      overlayElement.style.width = `${width}px`;
      overlayElement.style.height = `${height}px`;
    });
  }
  calculateOverlayControlSizesTakingIntoAccountMargin(thisWidgetElement, nextWidgetElement, previousWidgetElement) {
    let top;
    let height;
    const width = thisWidgetElement.offsetWidth;

    // If there is a previous widget, expand the overlay area up to the mid point between this widget and the previous one (split the margin between the two)
    if (previousWidgetElement) {
      const bottomOfPreviousWidget = previousWidgetElement.offsetTop + previousWidgetElement.offsetHeight;
      const topOfThisWidget = thisWidgetElement.offsetTop;
      const midPointBetween = (bottomOfPreviousWidget + topOfThisWidget) / 2;
      top = midPointBetween;
    } else {
      top = thisWidgetElement.offsetTop;
    }

    // If there is a next widget, expand the overlay area up to the mid point between this widget and the next one (split the margin between the two)
    if (nextWidgetElement) {
      const bottomOfThisWidget = thisWidgetElement.offsetTop + thisWidgetElement.offsetHeight;
      const topOfNextWidget = nextWidgetElement.offsetTop;
      const midPointBetween = (bottomOfThisWidget + topOfNextWidget) / 2;
      height = midPointBetween - top;
    } else {
      const bottomOfThisWidget = thisWidgetElement.offsetTop + thisWidgetElement.offsetHeight;
      height = bottomOfThisWidget - top;
    }
    return {
      top,
      height,
      width
    };
  }
  setOverlayElementRef({
    widgetData,
    overlayElement,
    thisWidgetElement,
    nextWidgetElement,
    previousWidgetElement
  }) {
    if (overlayElement) {
      this.overlays[widgetData.name] = {
        widgetData,
        overlayElement,
        thisWidgetElement,
        nextWidgetElement,
        previousWidgetElement
      };
    }
  }
  renderWidgetComponent(widgetData) {
    const {
      gates,
      bus,
      currentInpageMode,
      selectedModuleName,
      isAnythingSelected,
      highlightedWidgetId,
      scrollToModuleId,
      sidebarIsExpanded,
      renderLanguage,
      hasUndoRedoAccess,
      tellParentToHideModuleMenu,
      shouldShowFakeWidgetEditors,
      updateNodePositions,
      layoutSectionHoverPositionsClearedAndFrozen,
      layoutSectionHoverPositionsThawed,
      scopes,
      userId
    } = this.props;
    return WidgetWrapperContainer({
      widget: widgetData,
      gates,
      bus,
      currentInpageMode,
      selectedModuleName,
      isAnythingSelected,
      scrollToModuleId,
      renderLanguage,
      hasUndoRedoAccess,
      highlightedWidgetId,
      sidebarIsExpanded,
      tellParentToHideModuleMenu,
      shouldShowFakeWidgetEditors,
      updateNodePositions,
      layoutSectionHoverPositionsClearedAndFrozen,
      layoutSectionHoverPositionsThawed,
      scopes,
      userId
    });
  }
  renderWidgetWithinFlexColumn({
    index,
    thisWidgetElement,
    widgetData,
    isLastWidget
  }) {
    const {
      container,
      currentInpageMode,
      isDragging,
      updateFlexColumnContents,
      startDraggingFromFlexColumn,
      endDraggingFromFlexColumn
    } = this.props;
    const widgetHeight = thisWidgetElement.offsetHeight;
    const widgetWidth = thisWidgetElement.offsetWidth;
    const widgetTop = thisWidgetElement.offsetTop;
    if (!isDragging && currentInpageMode !== InpageModes.LAYOUT) {
      return this.renderWidgetComponent(widgetData);
    }
    return /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsxs(LameNullErrorHandlerInIframe, {
        children: [/*#__PURE__*/_jsx(BeforeBeginPortal, {
          id: `portal_${container.name}_${index}`,
          targetElement: thisWidgetElement,
          children: /*#__PURE__*/_jsxs("div", {
            children: [this.renderDropTargets(index), currentInpageMode === InpageModes.LAYOUT &&
            /*#__PURE__*/
            // @bend-expect trellis-tokenized
            _jsx("div", {
              className: "hs-private",
              children: /*#__PURE__*/_jsx(FlexColumnControlsOverlay, {
                $width: widgetWidth,
                $height: widgetHeight,
                $top: widgetTop,
                ref: overlayElement => this.setOverlayElementRef({
                  widgetData,
                  overlayElement,
                  thisWidgetElement
                }),
                children: /*#__PURE__*/_jsx(View, {
                  position: "relative",
                  blockSize: "100%",
                  inlineSize: "100%",
                  children: /*#__PURE__*/_jsx(FlexColumnDragAffordance, {
                    widgetName: widgetData.name,
                    widgetElement: thisWidgetElement,
                    containerName: container.name,
                    dragIndex: index,
                    updateFlexColumnContents: updateFlexColumnContents,
                    startDraggingFromFlexColumn: startDraggingFromFlexColumn,
                    endDraggingFromFlexColumn: endDraggingFromFlexColumn,
                    currentInpageMode: currentInpageMode
                  }, `dragAffordance_${widgetData.name}_${container.name}_${index}`)
                })
              })
            }), this.renderWidgetComponent(thisWidgetElement, widgetData)]
          })
        }, `portal_${container.name}_${widgetData.name}_${index}`), isLastWidget && /*#__PURE__*/_jsx(AfterEndPortal, {
          id: `portal_${container.name}_${index}_last_droptarget`,
          targetElement: thisWidgetElement,
          children: /*#__PURE__*/_jsx("div", {
            children: this.renderDropTargets(index + 1)
          })
        })]
      })
    }, `${container.name}_${widgetData.name}_${index}`);
  }
  renderDropTargets(index) {
    const {
      container,
      isDragging,
      updateCurrentDropTarget,
      widgetBeingDraggedIndex,
      widgetBeingDraggedParentContainer,
      currentTargetDropIndex
    } = this.props;
    if (!isDragging) {
      return null;
    }
    const flexDropColumnId = container.name;
    const isHoveringOverSameFlexColumnAsDraggedWidget = flexDropColumnId === widgetBeingDraggedParentContainer;
    const isHoveringAboveOrBelowSameIndexAsDraggedWidget = index === widgetBeingDraggedIndex || index === widgetBeingDraggedIndex + 1;
    if (isHoveringOverSameFlexColumnAsDraggedWidget && isHoveringAboveOrBelowSameIndexAsDraggedWidget) {
      return null;
    }
    return /*#__PURE__*/_jsx(FlexColumnEmptyDropPlaceHolder, {
      flexDropColumnId: flexDropColumnId,
      dropIndex: index,
      updateCurrentDropTarget: updateCurrentDropTarget,
      currentTargetDropIndex: currentTargetDropIndex
    });
  }
  renderEmptyFlexColumn() {
    const {
      container,
      containerElement,
      isDragging,
      updateCurrentDropTarget,
      currentTargetDropIndex
    } = this.props;
    if (!isDragging) {
      return null;
    }
    return /*#__PURE__*/_jsx(AfterBeginPortal, {
      id: `portal_${container.name}_empty`,
      targetElement: containerElement,
      namespace: "hs-private",
      children: /*#__PURE__*/_jsx(View, {
        zIndex: Z_INDEX_MAX_LAYERS.LEVEL_2,
        position: "relative",
        height: "100%",
        width: "100%",
        children: /*#__PURE__*/_jsx(FlexColumnEmptyDropPlaceHolder, {
          flexDropColumnId: container.name,
          dropIndex: 0,
          updateCurrentDropTarget: updateCurrentDropTarget,
          currentTargetDropIndex: currentTargetDropIndex
        })
      })
    }, `portal_${container.name}_empty`);
  }
  render() {
    const {
      container,
      inpageModulesInsideContainers
    } = this.props;
    return /*#__PURE__*/_jsx("div", {
      children: container.widgets.length === 0 ? this.renderEmptyFlexColumn() : flatten(container.widgets.map((widget, index) => {
        const {
          $widget
        } = getWidgetElement(widget);

        // If we can't find the widget in the DOM, it's likely that some external JS
        // has mucked with the DOM, and we should bail.
        if ($widget.length === 0) {
          return false;
        }
        const isLastWidget = index === container.widgets.length - 1;

        // Figure out next widget element
        let nextWidgetElement;
        if (container.widgets[index + 1]) {
          const nextWidget = getWidgetElement(container.widgets[index + 1]);
          if (nextWidget && nextWidget.$widget) {
            nextWidgetElement = nextWidget.$widget.get(0);
          }
        }

        // Figure out previous widget element
        let previousWidgetElement;
        if (container.widgets[index - 1]) {
          const prevWidget = getWidgetElement(container.widgets[index - 1]);
          if (prevWidget && prevWidget.$widget) {
            previousWidgetElement = prevWidget.$widget.get(0);
          }
        }
        // Switch from using the widget data structure in the containers.widgets array to the one from allWidgets, the schema is slightly different.
        const widgetData = findWhere(inpageModulesInsideContainers, {
          name: widget.name
        });

        // When deleting a module, inpageModulesInsideContainers updates before "container" does
        // allContainers (where container comes from) only gets updated on inpage editor start()
        if (!widgetData) {
          return null;
        }
        const args = {
          index,
          thisWidgetElement: $widget.get(0),
          nextWidgetElement,
          previousWidgetElement,
          widgetData,
          isLastWidget
        };
        return this.renderWidgetWithinFlexColumn(args);
      }))
    });
  }
}